import { useEffect, useRef, useCallback } from 'react';
import { WS_BASE_URL } from '../services/api';

const useTicketWebSocket = (ticketId, onNewComment, onTicketUpdate) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    if (!ticketId) return;

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const params = new URLSearchParams();
      const accessToken = localStorage.getItem('access_token');
      const applicationKey = process.env.REACT_APP_HELPDESK_API_KEY;
      const applicationUserId =
        localStorage.getItem('helpdesk_application_user_id') ||
        process.env.REACT_APP_HELPDESK_USER_ID;
      const externalUserId =
        localStorage.getItem('helpdesk_external_user_id') ||
        process.env.REACT_APP_HELPDESK_EXTERNAL_USER_ID;

      if (accessToken) {
        // Staff console auth: channels.auth.AuthMiddlewareStack only reads session
        // cookies, but the console is JWT-only, so pass the token explicitly for
        // TicketConsumer.resolve_user_from_token to validate.
        params.set('token', accessToken);
      }

      if (applicationKey) {
        params.set('application_key', applicationKey);
      }

      if (applicationUserId) {
        params.set('application_user_id', applicationUserId);
      } else if (externalUserId) {
        params.set('external_user_id', externalUserId);
      }

      const query = params.toString();
      const wsUrl = `${WS_BASE_URL}/ws/tickets/${ticketId}/${query ? `?${query}` : ''}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected for ticket:', ticketId);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_comment' && data.comment) {
            console.log('Received new comment via WebSocket:', data.comment);
            onNewComment(data.comment);
          } else if (data.type === 'ticket_update' && data.data) {
            console.log('Received ticket update via WebSocket:', data.data);
            if (onTicketUpdate) {
              onTicketUpdate(data.data);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        wsRef.current = null;

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`Reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1;
            connect();
          }, delay);
        } else {
          console.warn('Max reconnection attempts reached. WebSocket will not reconnect automatically.');
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [ticketId, onNewComment, onTicketUpdate]);

  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    reconnect: connect
  };
};

export default useTicketWebSocket;
